import { jsxs, jsx } from "react/jsx-runtime";
import React__default, { memo, useMemo } from "react";
import { ChatMessage } from "./ChatMessage.js";
const isUIMessage = (msg) => "id" in msg && "parts" in msg && Array.isArray(msg.parts);
function extractTextContent(parts) {
  return parts.filter((p) => p.type === "text" && "text" in p).map((p) => p.text).join("");
}
const SharedThreadDivider = ({ text }) => /* @__PURE__ */ jsxs(
  "div",
  {
    style: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      padding: "4px 0",
      margin: "8px 0"
    },
    children: [
      /* @__PURE__ */ jsx("div", { style: { flex: 1, height: "1px", backgroundColor: "rgba(0,0,0,0.12)" } }),
      /* @__PURE__ */ jsx(
        "span",
        {
          style: {
            fontSize: "13px",
            color: "rgba(0,0,0,0.4)",
            whiteSpace: "nowrap"
          },
          children: text
        }
      ),
      /* @__PURE__ */ jsx("div", { style: { flex: 1, height: "1px", backgroundColor: "rgba(0,0,0,0.12)" } })
    ]
  }
);
const MessageListV2 = memo(
  ({
    conversationId,
    messages,
    showTypingIndicator,
    streamingAIResponse,
    productMetadataMap,
    surveyMetadataMap,
    streamingProductGroups,
    currentStatus,
    previewSearchQueries,
    previewQueryProducts,
    isWaitingForFollowUpQueries,
    isActivelyStreaming,
    streamingStoreLocator,
    streamingLoginRequired,
    streamingDevInfo,
    onSignIn,
    followUpQuestions = [],
    bottomRef,
    onSearch,
    onUpdateFeedback,
    onVariantSelect,
    onSuggestionClick,
    apiBaseUrl,
    isSharedView,
    sharedByUserName,
    sourceConversationId,
    sourceCheckpointIndex,
    isPendingAssistantResponse,
    isPollingTimedOut,
    onRetry,
    pdpStacklineSku
  }) => {
    const displayMessages = useMemo(
      () => [
        ...messages,
        ...showTypingIndicator ? [
          {
            role: "assistant",
            content: "",
            timestamp: /* @__PURE__ */ new Date(),
            isTyping: true
          }
        ] : [],
        // Pending assistant response indicator (polling for backend response)
        ...isPendingAssistantResponse && !isPollingTimedOut && !showTypingIndicator ? [
          {
            role: "assistant",
            content: "",
            timestamp: /* @__PURE__ */ new Date(),
            isTyping: true
          }
        ] : [],
        // Add a streaming message if:
        // 1. We have streaming AI response content, OR
        // 2. We're actively streaming and have preview data (even if no AI response yet)
        ...streamingAIResponse || isActivelyStreaming && (previewSearchQueries.length > 0 || previewQueryProducts.length > 0) ? [
          {
            role: "assistant",
            content: streamingAIResponse,
            timestamp: /* @__PURE__ */ new Date(),
            // Only mark as streaming if we're still actively streaming
            // Once streaming stops, show actions even if we're still showing streamingAIResponse
            isStreaming: isActivelyStreaming
          }
        ] : []
      ],
      [
        messages,
        showTypingIndicator,
        streamingAIResponse,
        isActivelyStreaming,
        previewSearchQueries.length,
        previewQueryProducts.length,
        isPendingAssistantResponse,
        isPollingTimedOut
      ]
    );
    const topDividerText = isSharedView ? sharedByUserName ? `Shared with you by ${sharedByUserName}` : "Shared with you" : sourceConversationId ? "Your copy of a shared conversation" : null;
    return /* @__PURE__ */ jsxs(
      "div",
      {
        style: {
          width: "100%",
          maxWidth: "768px",
          display: "flex",
          flexDirection: "column",
          gap: "24px"
        },
        children: [
          topDividerText && /* @__PURE__ */ jsx(SharedThreadDivider, { text: topDividerText }),
          displayMessages.map((message, displayIndex) => {
            const hasStreamingMessage = !!streamingAIResponse || isActivelyStreaming && (previewSearchQueries.length > 0 || previewQueryProducts.length > 0);
            const isLastMessage = displayIndex === messages.length + (showTypingIndicator ? 1 : 0) + (hasStreamingMessage ? 1 : 0) - 1;
            const isTyping = "isTyping" in message && message.isTyping;
            const isStreaming = "isStreaming" in message && message.isStreaming;
            const showStreamingStoreLocator = isStreaming && streamingStoreLocator;
            const showStreamingLoginRequired = isStreaming && streamingLoginRequired;
            !isStreaming && !isTyping && "previewSearchQueries" in message && message.previewSearchQueries && message.previewSearchQueries.length > 0;
            const messageKey = isTyping ? "typing-indicator" : isStreaming ? `message-${messages.length}` : `message-${displayIndex}`;
            const messageContent = isUIMessage(message) ? extractTextContent(message.parts) : message.content;
            const messageUiParts = isUIMessage(message) ? message.parts : void 0;
            const messageFollowUpQueries = isUIMessage(message) ? message.parts.filter((p) => p.type === "data-follow-ups").flatMap((p) => {
              var _a;
              return ((_a = p.data) == null ? void 0 : _a.questions) ?? [];
            }) : "followUpQueries" in message ? message.followUpQueries : [];
            const showFollowUpQueries = !isTyping && !isStreaming && message.role === "assistant" && messageFollowUpQueries && messageFollowUpQueries.length > 0;
            const showForkBoundary = sourceConversationId && sourceCheckpointIndex !== void 0 && !isTyping && !isStreaming && displayIndex === sourceCheckpointIndex + 1;
            return /* @__PURE__ */ jsxs(React__default.Fragment, { children: [
              showForkBoundary && /* @__PURE__ */ jsx(SharedThreadDivider, { text: "Your messages below — only visible to you" }),
              /* @__PURE__ */ jsx(
                ChatMessage,
                {
                  bottomRef: isLastMessage ? bottomRef : void 0,
                  role: message.role,
                  content: messageContent,
                  isTyping,
                  isStreaming,
                  isLastMessage,
                  conversationId: conversationId || void 0,
                  messageIndex: "messageIndex" in message ? message.messageIndex : !isTyping && !isStreaming ? displayIndex : void 0,
                  feedback: "feedback" in message ? message.feedback : void 0,
                  followUpQueries: showFollowUpQueries ? messageFollowUpQueries : [],
                  nextUserMessage: (() => {
                    const nextMsg = messages[displayIndex + 1];
                    if (displayIndex >= messages.length - 1 || (nextMsg == null ? void 0 : nextMsg.role) !== "user") return void 0;
                    return isUIMessage(nextMsg) ? extractTextContent(nextMsg.parts) : nextMsg.content;
                  })(),
                  productMetadataMap,
                  surveyMetadataMap,
                  streamingProductGroups,
                  currentStatus: isLastMessage ? currentStatus : void 0,
                  previewSearchQueries: isStreaming && isLastMessage ? previewSearchQueries : "previewSearchQueries" in message ? message.previewSearchQueries || [] : [],
                  previewQueryProducts: isStreaming && isLastMessage ? previewQueryProducts : "previewQueryProducts" in message ? message.previewQueryProducts || [] : [],
                  isWaitingForFollowUpQueries: isLastMessage ? isWaitingForFollowUpQueries : false,
                  storeLocator: "storeLocator" in message ? message.storeLocator : void 0,
                  streamingStoreLocator: showStreamingStoreLocator ? streamingStoreLocator : void 0,
                  loginRequired: "loginRequired" in message ? message.loginRequired : void 0,
                  streamingLoginRequired: showStreamingLoginRequired ? streamingLoginRequired : void 0,
                  onSignIn,
                  orders: "orders" in message ? message.orders : void 0,
                  devInfo: isStreaming && streamingDevInfo ? streamingDevInfo : "devInfo" in message ? message.devInfo : void 0,
                  onSearch,
                  onUpdateFeedback,
                  onVariantSelect,
                  onSuggestionClick,
                  apiBaseUrl,
                  isSharedView,
                  uiMessageParts: messageUiParts,
                  pdpStacklineSku
                }
              )
            ] }, messageKey);
          }),
          isPollingTimedOut && onRetry && /* @__PURE__ */ jsxs(
            "div",
            {
              style: {
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "12px",
                padding: "24px 16px"
              },
              children: [
                /* @__PURE__ */ jsx(
                  "p",
                  {
                    style: {
                      fontSize: "14px",
                      color: "rgba(0,0,0,0.5)",
                      textAlign: "center",
                      margin: 0
                    },
                    children: "Response generation timed out. Please try again."
                  }
                ),
                /* @__PURE__ */ jsx(
                  "button",
                  {
                    type: "button",
                    onClick: onRetry,
                    style: {
                      padding: "8px 24px",
                      borderRadius: "20px",
                      border: "1px solid rgba(0,0,0,0.2)",
                      backgroundColor: "transparent",
                      cursor: "pointer",
                      fontSize: "14px",
                      color: "rgba(0,0,0,0.7)"
                    },
                    children: "Retry"
                  }
                )
              ]
            }
          )
        ]
      }
    );
  }
);
export {
  MessageListV2
};
//# sourceMappingURL=MessageListV2.js.map
